import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { AuthContext, type AdminIdentity, type AuthContextValue } from './AuthContext';
import { AUTH_UNAUTHORIZED_EVENT } from '../api/client';

// A sessão vive num cookie HttpOnly — o JavaScript NÃO consegue lê-la, e é
// exatamente isso que a torna segura. Logo, o estado aqui não é "o token", e
// sim "o que o servidor respondeu sobre a sessão": perguntamos ao
// `/api/auth/me` no boot e a cada 401 do proxy.
//
// Nada é guardado em localStorage/sessionStorage: um refresh reconfirma com o
// servidor, que é a única fonte de verdade.

export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminIdentity | null | undefined>(undefined);
  const [scopes, setScopes] = useState<string[]>([]);

  const checkSession = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me', {
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) {
        setAdmin(null);
        setScopes([]);
        return;
      }
      const body = (await res.json()) as {
        admin: AdminIdentity;
        scopes?: string[];
      };
      setAdmin(body.admin ?? null);
      setScopes(body.scopes ?? []);
    } catch {
      setAdmin(null);
      setScopes([]);
    }
  }, []);

  useEffect(() => {
    void checkSession();
  }, [checkSession]);

  const clearSession = useCallback(() => {
    setAdmin(null);
    setScopes([]);
  }, []);

  // O client dispara este evento quando o proxy responde 401 (sessão expirada
  // ou conta removida): o cookie já foi limpo pelo servidor, aqui só refletimos.
  useEffect(() => {
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, clearSession);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, clearSession);
  }, [clearSession]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!res.ok) {
      // Mensagem única para qualquer recusa — o servidor também não distingue.
      if (res.status === 429) {
        throw new Error('Muitas tentativas. Aguarde um instante e tente de novo.');
      }
      // 503 é o serviço de autenticação, não a credencial — dizer "senha
      // inválida" mandaria o operador tentar de novo para sempre.
      if (res.status >= 500) {
        throw new Error(
          'Serviço de autenticação indisponível. Avise a equipe técnica.'
        );
      }
      throw new Error('E-mail ou senha inválidos.');
    }

    const body = (await res.json()) as { admin: AdminIdentity | null };
    setAdmin(body.admin ?? null);
    // Os poderes vêm do /me (fonte do servidor), não do corpo do login.
    await checkSession();
  }, [checkSession]);

  const logout = useCallback(async () => {
    await fetch('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    clearSession();
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      admin,
      scopes,
      isChecking: admin === undefined,
      login,
      logout,
      clearSession
    }),
    [admin, scopes, login, logout, clearSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
